import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "Which of the following is most important for environmental well-being?",
    options: [
      "Feeling safe and secure",
      "Brand of car owned",
      "Social media following"
    ],
    correctAnswer: 0,
    explanation: "Feeling safe and secure in your environment is crucial for mental health. Unsafe environments create chronic stress and anxiety, while safe surroundings provide the foundation for psychological well-being."
  },
  {
    question: "What is the primary goal of environmental psychology?",
    options: [
      "To study how people interact with their physical and social environments",
      "To design more expensive buildings",
      "To promote outdoor advertising"
    ],
    correctAnswer: 0,
    explanation: "Environmental psychology studies the relationship between individuals and their physical and social environments, focusing on how these environments affect human behavior, emotions, and well-being."
  },
  {
    question: "Which environmental factor has the strongest impact on daily stress levels?",
    options: [
      "Noise pollution from traffic and neighbors",
      "The color of your walls",
      "The brand of your refrigerator"
    ],
    correctAnswer: 0,
    explanation: "Noise pollution is a significant environmental stressor that can disrupt sleep, concentration, and overall well-being. Chronic noise exposure has been linked to increased stress hormones and health issues."
  },
  {
    question: "What role does natural light play in environmental well-being?",
    options: [
      "It regulates circadian rhythms and improves mood",
      "It makes rooms look bigger",
      "It reduces electricity bills"
    ],
    correctAnswer: 0,
    explanation: "Natural light helps regulate our internal body clock (circadian rhythms), improves mood through vitamin D production, and enhances cognitive function and overall well-being."
  },
  {
    question: "Which aspect of the physical environment most affects social interactions?",
    options: [
      "The layout and design of shared spaces",
      "The type of flooring material",
      "The brand of furniture"
    ],
    correctAnswer: 0,
    explanation: "The design and layout of shared spaces can either facilitate or hinder social interactions. Well-designed spaces encourage communication and community building, while poor design can isolate individuals."
  }
];

const EnvironmentalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('environmentalM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Environmental well-being fundamentals
              </li>
              <li>
                WHOQOL-BREF environmental dimensions
              </li>
              <li>
                Environment-health connections
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your environmental wellness journey by exploring the ecology of daily life!</p>
            <p>Ready to examine how everyday environmental conditions impact your health and well-being?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/environmental-resources')}
          >
            Return to Environmental Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left environmental-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Environmental Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about environmental well-being and its importance.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/environmental-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule1QuizPage;

